import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE_URL = '/api/v1/auth';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth必须在AuthProvider内使用');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    // 检查本地存储中的token
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setAuthenticated(true);
        
        // 验证token是否有效
        checkAuth();
      } catch (error) {
        console.error('解析用户数据失败:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logout();
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/check`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.code === 1 && response.data.data.authenticated) {
        setAuthenticated(true);
        setUser(response.data.data.user);
      } else {
        logout();
      }
    } catch (error) {
      console.error('验证认证状态失败:', error);
      logout();
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/login`, {
        username,
        password
      });

      if (response.data.code === 1) {
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setAuthenticated(true);
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.msg };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || '登录失败，请检查网络连接' 
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        username,
        email,
        password
      });

      if (response.data.code === 1) {
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setAuthenticated(true);
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.msg };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || '注册失败，请检查网络连接' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setAuthenticated(false);
    
    // 调用后端登出API
    axios.post(`${API_BASE_URL}/logout`, {}, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }).catch(error => {
      console.error('登出API调用失败:', error);
    });
  };

  // 直接设置登录状态（用于登录页面）
  const setAuthState = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    setAuthenticated(true);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const value = {
    user,
    authenticated,
    loading,
    login,
    register,
    logout,
    checkAuth,
    getAuthHeaders,
    setAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};